import React, { useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { graphql } from 'gatsby';
import PropTypes from 'prop-types';
import Lottie from 'lottie-react';

import { containerStyles } from '../../styles/layout';
import { body, headers } from '../../styles/type';
import { colors } from '../../theme/colors';
import { debug } from '../../utilities/misc';
import { rem } from '../../utilities/style';

import Button from '../buttons/Button';
import Image from '../Image';
import TextRenderer from '../TextRenderer';
import { media } from '../../theme/media';

const Container = styled.div`
  position: relative;
  z-index: 1;
  padding: ${rem(120)} 0 ${rem(32)} 0;
  border-top: 1px solid ${colors.blue['100']};
  border-bottom: 1px solid ${colors.blue['100']};
`;

const LottieStyled = styled(Lottie)`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  z-index: -1;
`;

const ContentWrapper = styled.div`
  ${containerStyles.regular}
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const Headline = styled.h1`
  ${headers.h2}
  color: ${colors.blue['800']};
  max-width: ${rem(800)};
  margin-bottom: ${rem(16)};
`;

const Subhead = styled.p`
  ${body.large}
  color: ${colors.gray['600']};
  max-width: ${rem(800)};
  margin-bottom: ${rem(32)};
`;

const Logos = styled.div`
  display: flex;
  align-items: center;
  gap: ${rem(24)};
  max-width: ${rem(500)};
  margin-top: ${rem(80)};

  ${media('>small')} {
    gap: ${rem(40)};
  }
`;

const LogoWrapper = styled.div`
`;

const Logo = styled(Image)`
  max-height: ${rem(45)};
`;

const AnimatedHero = ({ headline, subhead, ctaText, ctaLink, logos, lottieAnimation, useH1 }) => {
  const [animationData, setAnimationData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const animationDataUrl = lottieAnimation?.file?.url;

    const getAnimationData = async () => {
      if (!animationDataUrl) {
        return;
      }

      try {
        const response = await fetch(animationDataUrl);
        const data = await response.json();

        if (mounted) {
          setAnimationData(data);
        }
      } catch (error) {
        debug('Error fetching Lottie animation data', error);
      }
    };

    getAnimationData();

    return () => {
      mounted = false;
    };
  }, [lottieAnimation]);

  return (
    <Container>
      {lottieAnimation?.file?.url && (
        <LottieStyled
          animationData={animationData}
          loop
          autoplay
          rendererSettings={{ preserveAspectRatio: 'xMidYMid slice' }}
        />
      )}
      <ContentWrapper>
        {headline && (
          <Headline as={useH1 ? 'h1' : 'h2'}>
            <TextRenderer text={headline} />
          </Headline>
        )}
        {subhead && (
          <Subhead>
            <TextRenderer text={subhead} />
          </Subhead>
        )}
        {ctaText && ctaLink && (
          <Button size="large" to={ctaLink}>
            <TextRenderer text={ctaText} />
          </Button>
        )}
        {logos && logos.length > 0 && (
          <Logos>
            {logos.map((logo, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <LogoWrapper key={index}>
                <Logo image={logo} alt={logo?.description} />
              </LogoWrapper>
            ))}
          </Logos>
        )}
      </ContentWrapper>
    </Container>
  );
};

AnimatedHero.propTypes = {
  headline: PropTypes.string,
  subhead: PropTypes.string,
  ctaText: PropTypes.string,
  ctaLink: PropTypes.string,
  logos: PropTypes.arrayOf(
    PropTypes.shape({
      // eslint-disable-next-line react/forbid-prop-types
      gatsbyImageData: PropTypes.object,
      description: PropTypes.string
    })
  ),
  // eslint-disable-next-line react/forbid-prop-types
  lottieAnimation: PropTypes.shape({
    file: PropTypes.shape({
      url: PropTypes.string
    })
  }),
  useH1: PropTypes.bool
};

export const query = graphql`
  fragment AnimatedHeroFragment on ContentfulAnimatedHero {
    id
    __typename
    headline
    subhead
    ctaText
    ctaLink
    logos {
      gatsbyImageData(height: 45, quality: 75)
      ...ImageAssetFragment
    }
    lottieAnimation {
      file {
        url
      }
    }
    personalizations {
      ...PersonalizationFragment
    }
  }
`;

export default AnimatedHero;
